import {
  deriveEffectiveTimeFrame,
  isContinuousScheduleType,
  isDayBasedTimeFrame,
  type ResourceGroupV3,
  type ScheduleType,
} from '@kouto/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import useStayQuote from 'hooks/useStayQuote';
import type { ITax } from 'types/experience.types';
import type { Session } from 'types/listings';
import {
  deriveContinuousSelection,
  describeStayLength,
  selectOpenSpans,
} from 'utils/continuousSelection';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';

interface UseContinuousStartingPriceOptions {
  /** Local "YYYY-MM-DD"; defaults to the earliest date among the spans. */
  date?: string;
  group: ResourceGroupV3;
  scheduleType?: ScheduleType | null;
  spans: readonly Session[];
}

export interface ContinuousStartingPrice {
  /** The cheapest tier's price for the shortest booking, as the group page shows it. */
  amount: number;
  isDayBased: boolean;
  /** "for 1 hour", "for 1 day", "for 1 night". */
  unitLabel: string;
}

/**
 * What the cheapest booking a guest can make on a flexible or multi-day group costs, with its unit.
 *
 * It prices the same default selection the group page opens on, through the same quote, so the card
 * never advertises a figure the next screen contradicts. A tier's own rate is a per-increment or
 * per-day rate, not the price of anything bookable. Null for a scheduled group, or while pricing.
 */
const useContinuousStartingPrice = ({
  date,
  group,
  scheduleType,
  spans,
}: UseContinuousStartingPriceOptions): ContinuousStartingPrice | null => {
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();

  const isContinuous = isContinuousScheduleType(scheduleType);
  const rule = group.availabilityRule ?? null;
  const timeFrame = deriveEffectiveTimeFrame(scheduleType, rule?.timeFrame);
  const openSpans = useMemo(() => selectOpenSpans(spans), [spans]);
  const selectionDate =
    date ?? openSpans.map((span) => span.startDateTime.split('T')[0]).sort()[0];

  const selection = useMemo(
    () =>
      isContinuous && rule && timeFrame && selectionDate
        ? deriveContinuousSelection({
            date: selectionDate,
            durations: group.durations ?? [],
            rule,
            spans: openSpans,
            timeFrame,
          })
        : null,
    [group.durations, isContinuous, openSpans, rule, selectionDate, timeFrame],
  );

  const tiers = useMemo(
    () =>
      (selection?.spans[0]?.priceTiers ?? []).map((tier) => ({
        id: tier.id,
        name: tier.name,
        participants: 0,
      })),
    [selection],
  );

  const { quote } = useStayQuote({
    enabled: isContinuous,
    experienceId: selection?.spans[0]?.experienceId,
    selection,
    tiers,
  });

  return useMemo(() => {
    const prices = Object.values(quote?.stayPriceByTierName ?? {}).filter(
      (price) => price > 0,
    );
    if (!selection || !timeFrame || prices.length === 0) {
      return null;
    }
    const taxes = (group.experiences?.[0]?.taxes as unknown as ITax[]) ?? [];
    return {
      amount: calculateAdvertisedTierPrice(Math.min(...prices), taxes),
      isDayBased: isDayBasedTimeFrame(timeFrame),
      unitLabel: translate('forLength', {
        length: describeStayLength(selection, timeFrame, translate, locale),
      }),
    };
  }, [
    calculateAdvertisedTierPrice,
    group.experiences,
    locale,
    quote,
    selection,
    timeFrame,
    translate,
  ]);
};

export default useContinuousStartingPrice;
